import {
  CursorType,
  ModalType,
  revertCursorType,
  setCursorType,
} from "@/store/globalSettingsSlice";
import {
  FIXED_IFRAME_MESSAGE_IDS,
  IFRAME_STATE,
  selectSelectedTempoElement,
  setIFrameState,
  setMessageResponse,
  setMultiSelectedElementHTML,
  setSelectedElementHTML,
} from "@/store/iframeSlice";
import { useCanvasPos, useCanvasStore } from "@/store/zustand/canvasStore";
import { EditTextChange } from "@/store/zustand/changeLedgerTypes";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { getTreeElementLookupToSend } from "@/utils/iframeUtils";
import { TempoElement } from "@/utils/tempoElement";
import { NavTreeNode } from "@/utils/types";
import {
  getUnexpiredToken,
  useActiveProject,
  useCanvasId,
} from "@/utils/utils";
import { MutableRefObject, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { useOnWheel } from "./useOnWheel";
import { useOnMouseMove } from "./useOnMouseMove";
import { useOnKeyDown, useOnKeyUp } from "./useKeyEvents";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import React from "react";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import { track } from "@/utils/events/tracker";
import { useDevServerStore } from "@/store/zustand/devServerStore";
import { getTempobookUrlFilePath } from "@/utils/sharedUtils";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { useIsChangePending } from "@/store/zustand/designCommandManagerStore";
import { useIframeStore } from "@/store/zustand/iframeStore";
import { handlePreviewCaptured, handlePreviewError } from "@/utils/imageUtils";
import { useSession } from "@clerk/clerk-react";

const SideEffectComponent = React.memo(() => {
  const { zoomPerc } = useCanvasPos();

  const sendMessageToIFrame = useIframeStore(
    (store) => store.sendMessageToIFrame
  );

  const setIframeMessagingPorts = useIframeStore(
    (store) => store.setIframeMessagingPorts
  );

  const canvasId = useCanvasId();

  useEffect(() => {
    // Clear when the canvasId changes
    setIframeMessagingPorts({});
  }, [canvasId]);

  // Send the zoomPerc to the iframe when it changes
  useEffect(() => {
    if (!zoomPerc) {
      return;
    }

    sendMessageToIFrame({
      type: "executeFunction",
      id: uuidv4(),
      payload: {
        functionName: "setZoomPerc",
        args: [zoomPerc],
      },
    });
  }, [zoomPerc]);

  return <></>;
});

/**
 * Hook to connect to and handle all post messaging to iframes
 */
export const useIframePostMessaging = (
  onIframeMessage: (e: any, storyboardId: string) => void,
  canvasRef: MutableRefObject<any>,
  iframesRef: MutableRefObject<{ [storyboardId: string]: any }>
) => {
  const dispatch = useDispatch<any>();

  const canvasId = useCanvasId();

  const showModal = useSelector((state: any) => state.globalSettings.showModal);
  const setPortForStoryboardId = useIframeStore(
    (store) => store.setPortForStoryboardId
  );

  const cursorType = useSelector(
    (state: any) => state.globalSettings.cursorType
  );
  const cursorTypeRef = useRef();
  useEffect(() => {
    cursorTypeRef.current = cursorType;
  }, [cursorType]);

  const setNavTreeForStoryboard = useCanvasStore(
    (store) => store.setNavTreeForStoryboard
  );
  const addElementKeysToNavNodeLookup = useCanvasStore(
    (store) => store.addElementKeysToNavNodeLookup
  );
  const addCodebaseIdToNavNodeLookup = useCanvasStore(
    (store) => store.addCodebaseIdToNavNodeLookup
  );
  const setStoryboardIdToCodebaseIds = useCanvasStore(
    (store) => store.setStoryboardIdToCodebaseIds
  );

  const executeCommand = useDesignCommandManager(
    (store) => store.executeCommand
  );

  const project = useActiveProject();
  const projectRef = useRef(project);
  useEffect(() => {
    projectRef.current = project;
  }, [project]);

  const setNewLayout = useLayoutStore((state) => state.setNewLayout);

  const onWheel = useOnWheel(canvasRef, iframesRef);

  const setFullHtmlForStoryboard = useCanvasStore(
    (store) => store.setFullHtmlForStoryboard
  );
  const setStoryboardIdToError = useDevServerStore(
    (store) => store.setStoryboardIdToError
  );

  const selectedElement = useSelector(selectSelectedTempoElement);
  const selectedElementRef = useRef(selectedElement);
  useEffect(() => {
    selectedElementRef.current = selectedElement;
  }, [selectedElement]);

  const cursorTypeFromKeyDown = useSelector(
    (state: any) => state.globalSettings.cursorTypeFromKeyDown
  );

  const cursorTypeFromKeyDownRef = useRef(cursorTypeFromKeyDown);

  useEffect(() => {
    cursorTypeFromKeyDownRef.current = cursorTypeFromKeyDown;
  }, [cursorTypeFromKeyDown]);

  const onKeyDown = useOnKeyDown();
  const onKeyUp = useOnKeyUp();

  const { session } = useSession();

  const processNavTree = (storyboardId: string, navTree: NavTreeNode) => {
    const elementKeyLookup = {};
    const codebaseIdLookup = {};
    const codebaseIds = new Set<string>();
    const addElementToLookupDict = (elem: NavTreeNode) => {
      if (!elem) {
        return;
      }

      // Need to do this conversion as all these functions are stripped from the object
      elem.tempoElement = elem.tempoElement
        ? TempoElement.fromOtherElement(elem.tempoElement)
        : TempoElement.empty();

      elementKeyLookup[elem.tempoElement.getKey()] = elem;
      if (elem.tempoElement.codebaseId) {
        const existingList =
          codebaseIdLookup[elem.tempoElement.codebaseId] || [];
        codebaseIdLookup[elem.tempoElement.codebaseId] = [
          ...existingList,
          elem,
        ];
        codebaseIds.add(elem.tempoElement.codebaseId);
      }

      if (elem.children) {
        elem.children.forEach((child) => {
          addElementToLookupDict(child);
        });
      }
    };
    addElementToLookupDict(navTree);

    setNavTreeForStoryboard(storyboardId, navTree);
    addElementKeysToNavNodeLookup(elementKeyLookup);
    addCodebaseIdToNavNodeLookup(codebaseIdLookup);
    setStoryboardIdToCodebaseIds(storyboardId, codebaseIds);
  };

  const onStartEditingText = (data: any, storyboardId: string) => {
    // setOngoingChange(true);
  };

  const onEditedText = (data: any, storyboardId: string) => {
    const { newText, oldText, key } = data.data;

    const element = TempoElement.fromKey(key);

    // setOngoingChange(false);

    if (newText === oldText) {
      return;
    }

    executeCommand(
      new EditTextChange({
        codebaseIdToEditText: element.codebaseId,
        newText,
        oldText,
      })
    );
  };

  const onInstantUpdateDone = (data: any, storyboardId: string) => {
    const change = useDesignCommandManager
      .getState()
      .undoStack.find((change) => change.id === data?.changeItem?.id);

    if (!change) {
      return;
    }

    change.markInstantUpdateFinished(
      data.instantUpdateData,
      data.instantUpdateSuccessful
    );
  };

  const onMouseMove = useOnMouseMove(canvasRef, iframesRef);

  const onIframeMessageInternal = (sbId: any, e: any) => {
    onIframeMessage(e, sbId);

    // Callback for when a message is received from an iframe
    if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.NAV_TREE) {
      processNavTree(sbId, e.data.navTree);
      if (e.data.outerHtml) {
        // Add random string to force update when html changes
        const randomStr = Math.random().toString(36).substring(7);
        setFullHtmlForStoryboard(
          sbId,
          e.data.outerHtml + `<!-- ${randomStr} -->`
        );
      }
    } else if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.WHEEL_EVENT) {
      onWheel(e.data.event, sbId);
    } else if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.MOUSE_MOVE_EVENT) {
      onMouseMove(e.data.event, sbId);
    } else if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.EDITED_TEXT) {
      onEditedText(e.data, sbId);
    } else if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.KEY_DOWN_EVENT) {
      onKeyDown(e.data.event);
    } else if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.KEY_UP_EVENT) {
      onKeyUp(e.data.event);
    } else if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.START_EDITING_TEXT) {
      onStartEditingText(e.data, sbId);
    } else if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.INSTANT_UPDATE_DONE) {
      onInstantUpdateDone(e.data, sbId);
    } else if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.SELECTED_ELEMENT_KEY) {
      if (
        !selectedElementRef.current?.storyboardId ||
        selectedElementRef.current.storyboardId === sbId
      ) {
        dispatch(setSelectedElementHTML(e.data.outerHTML));
      }
    } else if (
      e.data.id === FIXED_IFRAME_MESSAGE_IDS.MULTI_SELECTED_ELEMENT_KEYS
    ) {
      if (
        !selectedElementRef.current?.storyboardId ||
        selectedElementRef.current.storyboardId === sbId
      ) {
        dispatch(setMultiSelectedElementHTML(e.data.outerHTMLs?.join("")));
      }
    } else if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.EDIT_DYNAMIC_TEXT) {
      dispatch(setCursorType("pointer"));
      // @ts-expect-error TS2345
      setNewLayout(LeftTab.FileSelector, null);
    } else if (
      e.data.id === FIXED_IFRAME_MESSAGE_IDS.COMPONENT_PREVIEW_CAPTURED
    ) {
      const { componentKey, imageDataUrl } = e.data;
      getUnexpiredToken(session).then((supabaseJWT) => {
        if (!project || !supabaseJWT) {
          return;
        }
        handlePreviewCaptured(
          componentKey,
          imageDataUrl,
          project.id,
          supabaseJWT
        );
      });
    } else if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.COMPONENT_PREVIEW_ERROR) {
      const { componentKey: errorComponentKey, error } = e.data;
      handlePreviewError(errorComponentKey, error);
    } else if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.ERROR) {
      const messagesToSkip = [
        "Uncaught TypeError: Cannot read properties of null (reading 'useContext')",
        "Uncaught TypeError: Cannot read properties of null (reading 'useState')",
        "Uncaught TypeError: Cannot read properties of null (reading 'useMemo')",
        "Uncaught TypeError: Cannot read properties of null (reading 'useRef')",
        "Uncaught SyntaxError: Unexpected token '<'",
      ];

      const skipErrorMessage = messagesToSkip.some((message) =>
        e.data.error?.message?.includes(message)
      );
      if (skipErrorMessage) {
        track("STORYBOARD_SKIP_ERROR", {
          storyboard_id: sbId,
          error: e.data.error,
          type: "compile",
        });
        return;
      }

      track("STORYBOARD_ERROR", {
        storyboard_id: sbId,
        error: e.data.error,
        type: "compile",
      });
      setStoryboardIdToError(sbId, e.data.error);
    }

    dispatch(
      setMessageResponse({
        ...e.data,
        storyboardId: sbId,
      })
    );
  };

  const onIframeMessageInternalRef = useRef(onIframeMessageInternal);
  useEffect(() => {
    onIframeMessageInternalRef.current = onIframeMessageInternal;
  }, [onIframeMessageInternal]);

  /**
   * Triggered when either of the iframes have finished loading
   * @param iframe1 if the iframe that triggered this function is iframe 1
   * @param e
   * @returns
   */
  const onLoadIFrame = useCallback(
    async (
      iframeId: string,
      contentWindow: WindowProxy | null,
      storyboard: any,
      originalStoryboardUrl: string
    ) => {
      // Connect post message channel to this iframe
      const channel = new MessageChannel();
      const port1 = channel.port1;

      // Connect to the iframe
      // Note: especially when initializing TempoDevtools inside a useEffect, it can take a bit of time
      // for that code to run after onLoad is called. In testing on Jan 10, this took 80ms. Set to 500ms to
      // increase chance that it will be initialized by the time the message is sent
      // Update Jul 4 -> We no longer initialize inside a useEffect, so we can reduce the timeout to 100ms
      setTimeout(() => {
        if (!contentWindow) {
          return;
        }

        setPortForStoryboardId(storyboard.id, port1);

        port1.onmessage = (e: any) => {
          // Find the storyboard ID that this iframe is currently assigned to
          const assignments = useCanvasStore.getState().iframeAssignments;
          const storyboardId = Object.keys(assignments).find(
            (sbId) => assignments[sbId] === iframeId
          );

          if (!storyboardId) {
            console.error("Storyboard ID not found for iframe", iframeId);
            return;
          }

          onIframeMessageInternalRef.current(storyboardId, e);
        };

        // Transfer port2 to the iframe
        contentWindow.postMessage("init", "*", [channel.port2]);

        const treeElementLookup = useCodebaseStore.getState().treeElementLookup;

        const calculatedTempobookUrlPath = getTempobookUrlFilePath(
          canvasId,
          projectRef.current,
          storyboard.fullFilePath || "",
          storyboard.type === STORYBOARD_TYPES.STORY
            ? storyboard.storyComponentName || storyboard.name
            : storyboard.storyComponentName
        );

        let storyboardComponentElement = null;
        if (storyboard.type !== STORYBOARD_TYPES.APPLICATION) {
          // @ts-expect-error TS2322
          storyboardComponentElement = Object.values(treeElementLookup).find(
            (treeElement: any) => {
              return (
                (treeElement.type === "component" ||
                  (treeElement.type == "storybook-component" &&
                    calculatedTempobookUrlPath?.endsWith(
                      treeElement.componentName
                    ))) &&
                treeElement.filename === storyboard.fullFilePath
              );
            }
          );
        }

        const treeElementToSend = getTreeElementLookupToSend(treeElementLookup);

        const scopeLookup = useCodebaseStore.getState().scopeLookup;

        // Whenenver a new iframe is loaded switch back to non-drive mode if it was triggered from "d" keypress
        if (
          cursorTypeRef.current === "drive" &&
          cursorTypeFromKeyDownRef.current
        ) {
          dispatch(revertCursorType());
        }

        port1.postMessage({
          id: "REGISTER_EVENT_LISTENERS",
          type: "executeFunction",
          payload: {
            functionName: "initProject",
            args: [
              storyboard.id,
              treeElementToSend,
              scopeLookup,
              storyboardComponentElement,
              {
                driveModeEnabled: cursorTypeRef.current === CursorType.Drive,
                aiContextSelection: showModal === ModalType.AI,
              },
              storyboard.type,
              storyboard.fullFilePath || null,
              originalStoryboardUrl,
            ],
          },
        });

        dispatch(setIFrameState(IFRAME_STATE.SHOWING));
      }, 100);
    },
    [showModal, project, canvasId]
  );

  return { onLoadIFrame, SideEffectComponentInstance: <SideEffectComponent /> };
};
